/**
 * RegistrationScreen — the form a brand-new merchant fills in after OTP.
 *
 * Reached when `onboardingStatus === 'PENDING'`, which is the state
 * `/auth/verify-otp` leaves a phone number it has never seen: the backend
 * auto-creates a merchant and an OWNER user, both PENDING, then refuses every
 * business endpoint until a platform admin approves them (OnboardingGuard).
 *
 * Submitting moves the merchant to WAITING_APPROVAL. Ports the field set and
 * validation of BigxBookApp/src/screens/setup/RegistrationScreen.tsx, flattened
 * from that screen's three wizard steps into one form — a browser has the
 * vertical room a phone does not, and paging a seven-field form costs more than
 * it saves.
 */
import { useState, type FormEvent } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { HttpError } from '@/services/httpClient';
import { completeRegistration } from '@/auth/onboardingApi';
import { isValidGstin } from '@/reports/gst/gstStates';
import { Field, OnboardingCard, PRIMARY, QUIET, LABEL, FIELD } from './OnboardingCard';

/** Mirrors the mobile picker, which mirrors the backend's businessType values. */
const BUSINESS_TYPES = [
  { label: 'Retail Store / Kirana', value: 'RETAIL' },
  { label: 'Pharmacy / Medical', value: 'PHARMACY' },
  { label: 'Restaurant / Food', value: 'RESTAURANT' },
  { label: 'Electronics', value: 'ELECTRONICS' },
  { label: 'Clothing / Apparel', value: 'CLOTHING' },
  { label: 'Hardware / Building', value: 'HARDWARE' },
  { label: 'Stationery / Books', value: 'STATIONERY' },
  { label: 'Service Business', value: 'SERVICE' },
  { label: 'Other', value: 'OTHER' },
];

const PAN_RE = /^[A-Z]{5}\d{4}[A-Z]$/;

function describe(err: unknown): string {
  if (err instanceof HttpError) {
    const body = err.body as { message?: string | string[] } | null;
    const msg = Array.isArray(body?.message) ? body?.message[0] : body?.message;
    if (msg) return msg;
    return `Registration failed (${err.status}).`;
  }
  if (err instanceof TypeError) return 'Cannot reach the server. Check the connection.';
  return (err as Error)?.message ?? 'Something went wrong.';
}

export function RegistrationScreen({ onLogout }: { onLogout: () => void }) {
  const currentUser = useAppStore((s) => s.currentUser);
  const setCurrentUser = useAppStore((s) => s.setCurrentUser);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validated on the client too, not only by the DTO: a round-trip to be told
  // the GSTIN is malformed is a slow way to learn about a typo, and the server
  // rejects the whole form rather than the one bad field.
  const gstinBad = gstin.trim().length > 0 && !isValidGstin(gstin);
  const panBad = pan.trim().length > 0 && !PAN_RE.test(pan.trim().toUpperCase());
  const canSubmit = name.trim() && businessName.trim() && !gstinBad && !panBad && !busy;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setError(null);
    try {
      const res = await completeRegistration({
        name,
        email,
        businessName,
        businessType,
        gstin,
        pan,
      });

      // Persisting the new status is what moves the gate to the waiting screen.
      // Without this the tree would re-render on the same PENDING status and
      // show the form again, over a merchant the server has already advanced.
      if (currentUser) {
        await setCurrentUser({
          ...currentUser,
          name: res.user?.name ?? name.trim(),
          onboardingStatus: res.user?.onboardingStatus ?? 'WAITING_APPROVAL',
        });
      }
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <OnboardingCard
      width="lg"
      title="Tell us about your business"
      subtitle="We review every new business before it can start billing. This takes a few hours."
      error={error}
      footer={
        <button type="button" onClick={onLogout} className={`${QUIET} w-full`}>
          Sign out
        </button>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field
          id="reg-name"
          label="Your name"
          required
          autoFocus
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ajay Patel"
        />

        <Field
          id="reg-business"
          label="Business name"
          required
          autoComplete="organization"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="Patel Traders"
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="reg-type" className={LABEL}>
            Business type
          </label>
          <select
            id="reg-type"
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            className={FIELD}
          >
            <option value="">Select business type</option>
            {BUSINESS_TYPES.map((t) => (
              // Explicit dark styling: a native <option> list is painted by the
              // OS, not by the card's scoped tokens, so it would otherwise
              // render black-on-white inside a charcoal card.
              <option key={t.value} value={t.value} className="bg-ink-900 text-white">
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <Field
          id="reg-email"
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          hint="Optional. Where we send the approval decision."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="reg-gstin" className={LABEL}>
              GSTIN
            </label>
            <input
              id="reg-gstin"
              value={gstin}
              onChange={(e) => setGstin(e.target.value.toUpperCase())}
              className={FIELD}
              placeholder="22AAAAA0000A1Z5"
              maxLength={15}
              aria-invalid={gstinBad}
            />
            <p className={`text-label ${gstinBad ? 'text-danger-ink' : 'text-fg-tertiary'}`}>
              {gstinBad ? 'That is not a valid GSTIN.' : 'Optional. Needed to file GST returns.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="reg-pan" className={LABEL}>
              PAN
            </label>
            <input
              id="reg-pan"
              value={pan}
              onChange={(e) => setPan(e.target.value.toUpperCase())}
              className={FIELD}
              placeholder="ABCDE1234F"
              maxLength={10}
              aria-invalid={panBad}
            />
            <p className={`text-label ${panBad ? 'text-danger-ink' : 'text-fg-tertiary'}`}>
              {panBad ? 'That is not a valid PAN.' : 'Optional.'}
            </p>
          </div>
        </div>

        <button type="submit" disabled={!canSubmit} className={PRIMARY}>
          {busy ? 'Submitting…' : 'Submit for review'}
        </button>
      </form>
    </OnboardingCard>
  );
}
